import { supabase } from '@/integrations/supabase/client';
import { PostgrestError } from '@supabase/supabase-js';
import { RoadmapTemplate, PromptField, SidebarActivity, Link as RoadmapLink } from '@/types/roadmap';
import { normalizeAttachments } from '@/utils/promptFieldAttachments';

// Helper function to clean up options field
const cleanOptionsField = (options: PromptField['options']): string[] | undefined => {
  if (!options) return undefined;
  if (Array.isArray(options)) {
    // Ensure all elements are strings if it's an array of strings
    if (options.every(opt => typeof opt === 'string')) {
      return options as string[];
    }
    // If it's an array of {label, value} objects, this function as currently written would return undefined.
    // Depending on requirements, you might want to extract values or handle this differently.
    // For now, sticking to the original intent of returning string[] or undefined.
    return undefined;
  }
  // If options is an object but not an array (e.g., old malformed data), return undefined.
  return undefined;
};

// Define interfaces for raw Supabase data to handle snake_case
interface RawStepLink {
  label: string;
  url: string;
}

interface RawPromptField {
  id: string;
  prompt_module_id: string; // ensuring this is here
  field_id: string; // snake_case from DB
  label: string;
  tag: string;
  value: unknown;
  placeholder?: string;
  example?: string;
  position: number;
  lesson_library_id?: string;
  video_player_options?: unknown; // Consolidated from second definition
  attachments?: unknown;
  metadata?: unknown; // Added for completeness
  is_deleted: boolean;
}

interface RawPromptInstruction {
  id?: string; // Optional as it might not be in the first query if used differently, but needed for the second
  prompt_module_id?: string;
  instruction: string;
  position: number;
}

interface RawPromptModule {
  id: string;
  step_id: string;
  title: string;
  copy_button_text?: string;
  introduction?: string;
  final_output_format?: string;
}

// REMOVED DUPLICATE DEFINITIONS HERE

interface RawStepData {
  id: string;
  title: string;
  description?: string;
  is_open_default?: boolean;
  resources?: string;
  iframe_src?: string;
  download_link?: string;
  download_text?: string;
  // NEW: Generation settings (from LLM Testing Page)
  max_tokens?: number;
  temperature?: number;
  enable_reasoning?: boolean;
  enable_streaming?: boolean;
  json_mode?: boolean;
  // NEW: Tool configuration fields
  enable_web_search?: boolean;
  enable_image_generation?: boolean;
  enable_file_search?: boolean;
  enable_computer_use?: boolean;
  enable_code_execution?: boolean;
  enable_code_interpreter?: boolean;
  enable_x_search?: boolean;
  enable_document_search?: boolean;
  enable_collections_search?: boolean;
  enable_mcp_tools?: boolean;
  web_search_location?: string;
  web_search_context_size?: string;
  image_size?: string;
  image_quality?: string;
  image_format?: string;
  image_background?: string;
  image_moderation?: string;
  tool_choice?: string;
  parallel_tool_calls?: boolean;
  position: number;
  is_deleted: boolean;
  layout?: string;
  system_prompt?: string;
  dashboard_content?: any;
}

interface RawSidebarTool {
  id: string;
  label: string;
  url: string;
  description?: string;
  position: number;
  prompt_field_id?: string;
  step_id?: string;
}

interface RawSidebarActivity {
  id: string;
  title: string;
  description?: string;
  icon_placeholder?: string; // snake_case from DB
  href?: string;
  position: number;
}

interface RawSidebarExample {
  text: string;
  href?: string;
  description?: string;
  position: number;
}

interface RawSidebarData {
  id: string;
  sidebar_tools: RawSidebarTool[];
  sidebar_activities: RawSidebarActivity[];
  sidebar_examples: RawSidebarExample[];
}

function parseMissingColumnFromPostgrestError(error: PostgrestError | null): string | null {
  if (!error) return null;
  const msg = error.message || '';

  // Examples:
  // - "column steps.max_tokens does not exist"
  // - "column \"max_tokens\" does not exist"
  // - "column \"max_tokens\" of relation \"steps\" does not exist"
  const m1 = msg.match(/column\s+[a-zA-Z0-9_]+\.(\w+)\s+does not exist/i);
  if (m1?.[1]) return m1[1];

  const m2 = msg.match(/column\s+"(\w+)"\s+does not exist/i);
  if (m2?.[1]) return m2[1];

  const m3 = msg.match(/column\s+"?(\w+)"?\s+of relation\s+"?[a-zA-Z0-9_]+"?\s+does not exist/i);
  if (m3?.[1]) return m3[1];

  return null;
}

export async function getTemplate(id: string): Promise<RoadmapTemplate | null> {
  try {
    console.log(`getTemplate: Fetching template for id: ${id}`);

    // Get the basic template data
    const { data: templateData, error: templateError } = await supabase
      .from('roadmap_templates')
      .select('*')
      .eq('id', id)
      .single();

    if (templateError) {
      console.error(`getTemplate: Error fetching template data for ${id}:`, templateError);
      throw templateError;
    }

    if (!templateData) {
      console.warn(`getTemplate: No template found with id: ${id}`);
      return null;
    }

    // Get steps with all fields
    let stepsData: RawStepData[] | null = null;

    const { data, error: stepsError } = await supabase
      .from('steps')
      .select('*')
      .eq('roadmap_id', id)
      .eq('is_deleted', false)
      .order('position');

    if (stepsError) {
      console.error(`getTemplate: Error fetching steps for ${id}:`, stepsError);
      throw stepsError;
    }

    stepsData = data as unknown as RawStepData[];

    // Get prompt modules and fields separately (nested queries weren't working)
    // First get all step IDs for this template
    const stepIds = stepsData?.map(step => step.id) || [];

    // Initialize related records
    let promptModulesData: any[] | null = null;
    let promptFieldsData: any[] | null = null;
    let promptInstructionsData: any[] | null = null;
    let linksData: any[] | null = null;

    if (stepIds.length > 0) {
      // Get links for the steps
      const { data: stepLinksData, error: linksError } = await supabase
        .from('links')
        .select('id, step_id, label, url')
        .in('step_id', stepIds)
        // No position column on links, so we don't order by position, we can order by created_at
        .order('created_at', { ascending: true });

      if (linksError) {
        console.error(`getTemplate: Error fetching links for ${id}:`, linksError);
        throw linksError;
      }
      linksData = stepLinksData;

      const { data: modulesData, error: promptModulesError } = await supabase
        .from('prompt_modules')
        .select(`
          id,
          step_id,
          title,
          copy_button_text,
          introduction,
          final_output_format
        `)
        .in('step_id', stepIds);

      if (promptModulesError) {
        console.error(`getTemplate: Error fetching prompt modules for ${id}:`, promptModulesError);
        throw promptModulesError;
      }

      promptModulesData = modulesData;

      // Get prompt fields
      const { data: fieldsData, error: promptFieldsError } = await supabase
        .from('prompt_fields')
        .select(`
          id,
          prompt_module_id,
          field_id,
          label,
          tag,
          value,
          placeholder,
          example,
          position,
          lesson_library_id,
          attachments,
          video_player_options,
          metadata,
          client_id,
          is_deleted
        `)
        .in('prompt_module_id', promptModulesData?.map(pm => pm.id) || []);

      if (promptFieldsError) {
        console.error(`getTemplate: Error fetching prompt fields for ${id}:`, promptFieldsError);
        throw promptFieldsError;
      }

      console.log('📎 DB_QUERY: Raw field data from database:', fieldsData?.find(f => f.id === 'f9b3c339-5e6d-4bd7-bd9e-330df97fa2ec'));

      promptFieldsData = fieldsData;

      // Get prompt instructions
      const { data: instructionsData, error: promptInstructionsError } = await supabase
        .from('prompt_instructions')
        .select(`
          id,
          prompt_module_id,
          instruction,
          position
        `)
        .in('prompt_module_id', promptModulesData?.map(pm => pm.id) || []);

      if (promptInstructionsError) {
        console.error(`getTemplate: Error fetching prompt instructions for ${id}:`, promptInstructionsError);
        throw promptInstructionsError;
      }

      promptInstructionsData = instructionsData;
    } else {
      console.log('getTemplate: No steps found, skipping prompt modules');
    }

    // Get sidebar data
    const { data: sidebarData, error: sidebarError } = await supabase
      .from('sidebars')
      .select(`
        id,
        sidebar_tools (
          id,
          label,
          url,
          description,
          position,
          prompt_field_id,
          step_id
        ),
        sidebar_activities (
          id,
          title,
          description,
          icon_placeholder,
          href,
          position
        ),
        sidebar_examples (
          text,
          href,
          description,
          position
        )
      `)
      .eq('roadmap_id', id)
      .single() as { data: RawSidebarData | null; error: PostgrestError | null };

    const sidebarToolsByField = new Map<string, RawSidebarTool[]>();
    const globalSidebarTools: RawSidebarTool[] = [];



    (sidebarData?.sidebar_tools || []).forEach((tool) => {
      if (tool.prompt_field_id) {

        const list = sidebarToolsByField.get(tool.prompt_field_id) || [];
        list.push(tool);
        sidebarToolsByField.set(tool.prompt_field_id, list);
      } else {
        globalSidebarTools.push(tool);
      }
    });



    // Process steps data
    const processedSteps = (stepsData || [])
      .filter(step => step.is_deleted === false) // Ensure is_deleted steps are filtered out
      .map((step: RawStepData) => {
        const processedStep = {
          id: step.id,
          title: step.title,
          description: step.description || '',
          isOpenDefault: step.is_open_default || false,
          layout: step.layout || 'default',
          systemPrompt: step.system_prompt || '',
          resources: step.resources || '',
          maxTokens: step.max_tokens || 4096,
          temperature: step.temperature ?? 0.7,
          enableReasoning: step.enable_reasoning || false,
          enableStreaming: step.enable_streaming !== false,
          jsonMode: step.json_mode || false,
          enableWebSearch: step.enable_web_search !== false,
          enableImageGeneration: step.enable_image_generation !== false,
          enableFileSearch: step.enable_file_search === true,
          enableComputerUse: step.enable_computer_use || false,
          enableCodeExecution: step.enable_code_execution || false,
          enableCodeInterpreter: step.enable_code_interpreter || false,
          enableXSearch: step.enable_x_search || false,
          enableDocumentSearch: step.enable_document_search || false,
          enableCollectionsSearch: step.enable_collections_search || false,
          enableMcpTools: step.enable_mcp_tools || false,
          webSearchLocation: step.web_search_location || 'US',
          webSearchContextSize: step.web_search_context_size || 'medium',
          imageSize: step.image_size || 'auto',
          imageQuality: step.image_quality || 'high',
          imageFormat: step.image_format || 'png',
          imageBackground: step.image_background || 'auto',
          imageModeration: step.image_moderation || 'auto',
          toolChoice: step.tool_choice || 'auto',
          parallelToolCalls: step.parallel_tool_calls !== false,
          dashboard_content: step.dashboard_content || null,
          walkthrough: {
            iframeSrc: step.iframe_src || '',
            downloadLink: step.download_link || '',
            downloadText: step.download_text || 'Download Walkthrough',
            title: '', // These were empty in previous versions, maintaining consistency
            videoUrl: '',
            description: ''
          },
          links: (linksData?.filter(link => link.step_id === step.id) || []).map(link => ({
            label: link.label,
            url: link.url
          })),
          promptModule: (() => {
            // Find the prompt module for this step
            const promptModule = promptModulesData?.find(pm => pm.step_id === step.id);
            if (!promptModule) return undefined;

            // Get fields for this prompt module
            const fields = promptFieldsData
              ?.filter(field => field.prompt_module_id === promptModule.id && !field.is_deleted)
              .sort((a, b) => a.position - b.position)
              .map(field => {
                // Debug logging for interactive_prompt fields
                if (field.tag === 'interactive_prompt') {
                  console.log('🎯 ADMIN_LOAD: Found interactive_prompt field:', {
                    id: field.id,
                    label: field.label,
                    tag: field.tag,
                    is_deleted: field.is_deleted,
                    placeholder: field.placeholder,
                    fullRawField: field
                  });
                }

                const fieldSpecificTools = sidebarToolsByField.get(field.id) || [];
                const sortedFieldTools = [...fieldSpecificTools].sort((a, b) => a.position - b.position);
                const sidebarToolLinks = sortedFieldTools.map(tool => ({
                  label: tool.label,
                  url: tool.url,
                  description: tool.description
                }));

                // Use sidebar_tools as the source of truth for video field links
                // The video_player_options in prompt_fields table is only for UI state during editing
                // The persisted links are ALWAYS in sidebar_tools table
                const videoPlayerLinks = sidebarToolLinks;


                const meta = (field.metadata || {}) as Record<string, any>;
                const mappedField = {
                  id: field.id,
                  client_id: field.id,
                  label: field.label,
                  tag: field.tag,
                  placeholder: field.placeholder,
                  example: field.example,
                  position: field.position,
                  value: field.value,
                  options: Array.isArray(meta.options) ? meta.options : undefined,
                  video_player_options: videoPlayerLinks.length > 0 ? { links: videoPlayerLinks } : undefined,
                  lesson_library_id: field.lesson_library_id,
                  attachments: normalizeAttachments(field.attachments),
                  metadata: field.metadata,
                  // AI Companion config (hydrated from metadata JSONB)
                  aiCompanionEnabled: meta.aiCompanionEnabled || false,
                  aiCompanionType: meta.aiCompanionType || undefined,
                  aiCompanionUrl: meta.aiCompanionUrl || undefined,
                  aiCompanionId: meta.aiCompanionId || undefined,
                  aiCompanionName: meta.aiCompanionName || undefined,
                  aiCompanionSystemPrompt: meta.aiCompanionSystemPrompt || undefined,
                  aiCompanionShowInstructions: meta.aiCompanionShowInstructions || false,
                  aiCompanionHtml: meta.aiCompanionHtml || undefined,
                } as PromptField;

                // Debug logging for mapped interactive_prompt fields
                if (field.tag === 'interactive_prompt') {
                  console.log('🎯 ADMIN_MAPPED: Final mapped field:', {
                    id: mappedField.id,
                    label: mappedField.label,
                    tag: mappedField.tag,
                    slot: mappedField.metadata?.slot,
                    tabId: mappedField.metadata?.tabId
                  });
                }

                return mappedField;
              }) || [];

            // Get instructions for this prompt module
            const instructions = promptInstructionsData
              ?.filter(instruction => instruction.prompt_module_id === promptModule.id)
              .sort((a, b) => a.position - b.position)
              .map(instruction => instruction.instruction) || [];

            return {
              id: promptModule.id,
              title: promptModule.title || '',
              copyButtonText: promptModule.copy_button_text || 'Copy Entire Prompt',
              introduction: promptModule.introduction || '',
              finalOutputFormat: promptModule.final_output_format || '',
              fields,
              instructions
            };
          })()
        };
        return processedStep;
      });

    // Process sidebar data
    const processedSidebar = sidebarData ? {
      usefulTools: globalSidebarTools
        .sort((a: RawSidebarTool, b: RawSidebarTool) => a.position - b.position)
        .map((tool: RawSidebarTool) => ({
          label: tool.label,
          url: tool.url,
          description: tool.description
        })),
      bonusActivities: (sidebarData.sidebar_activities || [])
        .sort((a: RawSidebarActivity, b: RawSidebarActivity) => a.position - b.position)
        .map((activity: RawSidebarActivity) => ({
          id: activity.id,
          title: activity.title,
          description: activity.description,
          iconPlaceholder: activity.icon_placeholder,
          href: activity.href
        } as SidebarActivity)),
      examples: (sidebarData.sidebar_examples || [])
        .sort((a: RawSidebarExample, b: RawSidebarExample) => a.position - b.position)
        .map((example: RawSidebarExample) => ({
          text: example.text,
          href: example.href,
          description: example.description
        }))
    } : { usefulTools: [], bonusActivities: [], examples: [] };

    const finalTemplate: RoadmapTemplate = {
      id: templateData.id,
      title: templateData.title,
      description: templateData.description || '',
      pageTitle: templateData.page_title,
      embedUrl: templateData.embed_url,
      embedTitle: templateData.embed_title,
      embedCode: templateData.embedcode || '',
      lessonHtml: templateData.lessonhtml || '',
      tags: templateData.tags || [],
      accessType: templateData.access_type,
      isPublic: templateData.is_public,
      steps: processedSteps,
      sidebar: processedSidebar
    };

    // Debug: Count interactive_prompt fields in final template
    const totalInteractiveFields = finalTemplate.steps.reduce((count, step) => {
      const interactiveFields = step.promptModule?.fields?.filter(f => f.tag === 'interactive_prompt') || [];
      return count + interactiveFields.length;
    }, 0);

    if (totalInteractiveFields > 0) {
      console.log(`🎯 ADMIN_LOAD: Template "${finalTemplate.title}" loaded with ${totalInteractiveFields} interactive_prompt fields`);
    }

    // console.log('[getTemplate] Template data after cleaning (full query restored):', finalTemplate);

    return finalTemplate;
  } catch (error) {
    console.error('getTemplate error (attempting to load all optional fields):', error);
    throw error;
  }
}
